import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { authService } from "../../../services/authService";
import { setCredentials } from "../store/authSlice";
import "./r_sign_in 1.css";

const R_ResetPassword: React.FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const rawEmail = searchParams.get("email") || "";
    const email = useMemo(() => rawEmail.replace(/ /g, "+"), [rawEmail]);
    const token = useMemo(() => searchParams.get("token") || "", [searchParams]);
    const isActivationFlow = !!email && !!token;

    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [activatedAuth, setActivatedAuth] = useState<any>(null);

    const isDisabled =
        loading ||
        !token ||
        !email ||
        !newPassword ||
        !confirmPassword ||
        newPassword !== confirmPassword;

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (loading || isDisabled) return;
        setError(null);

        try {
            setLoading(true);
            const response = await authService.activate({
                email,
                token,
                new_password: newPassword,
                confirm_password: confirmPassword,
            });
            setActivatedAuth(response);
            if (isActivationFlow) {
                setIsSuccess(true);
            } else {
                navigate("/login", { replace: true });
            }
        } catch (err: any) {
            setError(err?.message || "Activation failed");
        } finally {
            setLoading(false);
        }
    };

    const handleGoToDashboard = () => {
        if (!activatedAuth) return;
        const isAdmin =
            activatedAuth?.role_id === 1 ||
            activatedAuth?.role_name?.toLowerCase() === "admin";

        const isInactiveSubUser =
            activatedAuth?.role_id === 6 &&
            (activatedAuth?.account_status === "inactive" || activatedAuth?.is_verified === false);

        if (isInactiveSubUser) {
            navigate(
                "/login?message=" + encodeURIComponent("Your account is inactive or not verified."),
                { replace: true }
            );
            return;
        }

        dispatch(setCredentials(activatedAuth));
        navigate(isAdmin ? "/user-management?tab=user-roles" : "/dashboard", { replace: true });
    };

    return (
        <div className="container">
            <div className="keep-box-in-center">
                <img src="/images/right-emails-logo.svg" className="form-logo" alt="Right Emails" />

                <div className="form-box">
                    {!isSuccess ? (
                        <>
                            <p className="form-header">Set Your Password</p>
                            {!!email && <p className="form-information-e">Email: {email}</p>}

                            {error && <p className="error-text">{error}</p>}

                            <form onSubmit={handleSubmit}>
                                <div className="tch-section password-field">
                                    <label className="r-form-label">New Password</label>
                                    <div className="password-wrapper">
                                        <input
                                            type={showNew ? "text" : "password"}
                                            name="new_password"
                                            className="r-form-email"
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            required
                                            disabled={loading}
                                        />
                                        <img
                                            src={showNew ? "/images/grey-eye.png" : "/images/md-eye-off.png"}
                                            className="eye-icon"
                                            onClick={() => setShowNew(!showNew)}
                                        />
                                    </div>
                                </div>

                                <div className="tch-section password-field">
                                    <label className="r-form-label">Confirm Password</label>
                                    <div className="password-wrapper">
                                        <input
                                            type={showConfirm ? "text" : "password"}
                                            name="confirm_password"
                                            className="r-form-email"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            required
                                            disabled={loading}
                                        />
                                        <img
                                            src={showConfirm ? "/images/grey-eye.png" : "/images/md-eye-off.png"}
                                            className="eye-icon"
                                            onClick={() => setShowConfirm(!showConfirm)}
                                        />
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    className={`form-submit ${loading ? "disabled" : ""}`}
                                    disabled={loading || isDisabled}
                                >
                                    {loading ? "Confirming..." : "Confirm"}
                                </button>
                            </form>

                            {!isActivationFlow && (
                                <p className="asking">
                                    Don't have an account?{" "}
                                    <span className="ask-sign-up" onClick={() => navigate("/signup")}>
                                        Sign Up
                                    </span>
                                </p>
                            )}
                        </>
                    ) : (
                        /* ✅ THANK YOU DIV */
                        <div className="thank-you-box">
                            <p className="form-header-r">Welcome</p>
                            <p className="form-information-r">
                                Your account has been activated!
                            </p>

                            <div className="d-flex flex-row justify-content-center r-crdit-box">
                                <div className="r-box-blue">
                                    <p className="r-credit-assigned">Credits Assigned</p>
                                    <p className="r-number">
                                        {typeof activatedAuth?.credits === "number"
                                            ? activatedAuth.credits.toLocaleString("en-US")
                                            : activatedAuth?.credits ?? "-"}
                                    </p>
                                </div>
                                <div>
                                    <img src="/images/d-line.png" style={{ height: "30px", margin: "5px 0 0" }} />
                                </div>
                                <div className="r-box-blue">
                                    <p className="r-credit-assigned">Role</p>
                                    <p className="r-number">{activatedAuth?.role_name ?? "Member"}</p>
                                </div>
                            </div>

                            <button
                                className="form-submit"
                                onClick={handleGoToDashboard}
                            >
                                Go to Dashboard
                            </button>
                        </div>
                    )}
                </div>
            </div>

        </div>
    );
};

export default R_ResetPassword;